import type { ProfileConfig } from "@worker/config-schema";
import { mountAvatar } from "../components/avatar";
import { mountUsername } from "../components/username";
import { mountBadges } from "../components/badges";
import { mountLocation } from "../components/location";
import { mountTypewriter } from "../components/typewriter";
import { mountViewCounter } from "../components/view-counter";
import { mountCard } from "../components/card";
import { mountSocials } from "../components/socials";
import { mountLinks } from "../components/links";
import { mountAudioPlayer } from "../components/audio-player";
import { mountContactTrigger } from "../components/contact-modal";
import { mountWidgets } from "../components/widgets/container";
import { mountInlineClock } from "../components/inline-clock";

/**
 * Shared component assembly. Each template imports this and arranges the
 * returned parts into its own layout. All parts are optional — templates
 * pick and choose.
 */
export type ProfileParts = {
  card: HTMLElement;
  avatar: HTMLElement;
  username: HTMLElement;
  badges: HTMLElement;
  description: HTMLElement;
  location: HTMLElement | null;
  viewCounter: HTMLElement | null;
  inlineClock: HTMLElement | null;
  contact: HTMLElement | null;
  socials: HTMLElement | null;
  links: HTMLElement | null;
  widgets: HTMLElement | null;
  audio: HTMLElement | null;
  /**
   * Routes a meta element by its placement config. Templates call this
   * instead of directly appending viewCounter / location / clock /
   * contact — any element the user placed at a corner gets siphoned off
   * into a floating zone and returns `null` from the inline slot.
   */
  meta: {
    getInline(kind: "views" | "location" | "clock" | "contact"): HTMLElement | null;
    /** Must be called after the card has its normal content appended. */
    applyCornerZones(): void;
  };
};

export function renderProfileParts(
  config: ProfileConfig,
  opts: { gateOpened: Promise<void> },
): ProfileParts {
  const trimmedLocation = (config.profile.location ?? "").trim();

  const card = mountCard(config.card);
  const viewCounter = config.profile.hideViewCounter ? null : mountViewCounter();
  const inlineClock = mountInlineClock(config.profile.inlineClock);
  const location = trimmedLocation ? mountLocation(trimmedLocation) : null;
  const contact = mountContactTrigger(config.contact);

  // Route each meta element to its configured placement. Non-"inline"
  // elements are wrapped in a corner zone and appended on `applyCornerZones`.
  const placement = config.profile.metaPlacement;
  const pending: Array<{
    zone: Exclude<typeof placement.views, "inline" | "hidden">;
    node: HTMLElement;
  }> = [];
  function route(
    kind: "views" | "location" | "clock" | "contact",
    node: HTMLElement | null,
  ): HTMLElement | null {
    if (!node) return null;
    const place = placement[kind];
    if (place === "hidden") return null;
    if (place === "inline") return node;
    pending.push({ zone: place, node });
    return null;
  }

  const parts: ProfileParts = {
    card,
    avatar: mountAvatar(config.profile.avatar),
    username: mountUsername(config.profile),
    badges: mountBadges(config.profile.badges),
    description: mountTypewriter(config.profile.description, config.motion),
    location,
    viewCounter,
    inlineClock,
    contact,
    socials: mountSocials(config.socials),
    links: mountLinks(config.links),
    widgets: mountWidgets(config.widgets, config.profile.inlineClock),
    audio: mountAudioPlayer(config.audio, opts.gateOpened),
    meta: {
      getInline(kind) {
        switch (kind) {
          case "views": return route("views", viewCounter);
          case "location": return route("location", location);
          case "clock": return route("clock", inlineClock);
          case "contact": return route("contact", contact);
        }
      },
      applyCornerZones() {
        // Group routed nodes by corner.
        const zones: Record<string, HTMLElement[]> = {};
        for (const { zone, node } of pending) {
          (zones[zone] ||= []).push(node);
        }
        for (const [zone, nodes] of Object.entries(zones)) {
          const el = document.createElement("div");
          el.className = `card__corner card__corner--${zone}`;
          for (const n of nodes) el.append(n);
          card.append(el);
        }
      },
    },
  };
  return parts;
}
